import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { Toast } from 'components';
import { TOAST_TYPES_ENUM } from 'enums';
import { queryClient } from 'App';
import { mutateWithOffline } from 'utils/mutateWithOffline';
import { upsertOfflineMeasurementClientInfo } from 'hooks/useOfflineMeasurementClientInfo';
import { resolveOfflineUuid } from 'hooks/useOfflineUuidMap';

type GeneralInfoData = {
  measurementUuid: string;
  typeOfInstallationUuids: Array<{ uuid: string, comment?: string }>;
  spoutLevel: 'fromTheProject' | 'specifiedByInvestor' | 'specifiedByContractor' | 'measuredFromStairLevel';
  typeOfStars?: 'concrete' | 'selfSupporting';
  starsMeasurement?: string;
  roomListUuids: Array<{ uuid: string, measurement: string }>;
  kitchenWindowConnectedToTheCountertop: boolean;
  kitchenWindowConnectedToTheCountertopMeasurement?: string;
  doesTheClientPlanBlindsShutters: boolean;
  preparationOfOpeningsForTripleLayerInstallation: string;
  changeOfOpeningDimensions: string;
  constructionSiteCleanupInvestor: boolean;
  typeOfBlindInstallationUuids?: Array<{ uuid: string, comment?: string }>;
  extraWorkUuids?: any 
};

const usePutGenerallInfoMeasurement = (
  options?: UseMutationOptions<any, Error, GeneralInfoData>
) => {
  return useMutation({
    ...options,
    mutationKey: ['editGeneralInfo'],
    mutationFn: async (data) => {
      return mutateWithOffline<any>(
        'PUT',
        `/measurement/client-info`,
        data,
        {
          entityType: 'measurement',
          dedupeKey: `measurement:client-info:${resolveOfflineUuid(data.measurementUuid)}`,
          simulateResponse: ({ tempId }) => {
            const now = new Date().toISOString();
            const effectiveUuid = resolveOfflineUuid(data.measurementUuid);
            upsertOfflineMeasurementClientInfo({
              measurementUuid: effectiveUuid,
              spoutLevel: data.spoutLevel,
              kitchenWindowConnectedToTheCountertop:
                data.kitchenWindowConnectedToTheCountertop,
              doesTheClientPlanBlindsShutters: data.doesTheClientPlanBlindsShutters,
              preparationOfOpeningsForTripleLayerInstallation:
                data.preparationOfOpeningsForTripleLayerInstallation,
              changeOfOpeningDimensions: data.changeOfOpeningDimensions,
              constructionSiteCleanupInvestor: data.constructionSiteCleanupInvestor,
              typeOfInstallationUuids: (data.typeOfInstallationUuids || []) as any,
              typeOfBlindInstallationUuids:
                (data.typeOfBlindInstallationUuids || []) as any,
              roomListUuids: (data.roomListUuids || []) as any,
              typeOfStars: (data as any).typeOfStars ?? null,
              starsMeasurement: (data as any).starsMeasurement ?? null,
              kitchenWindowConnectedToTheCountertopMeasurement:
                (data as any).kitchenWindowConnectedToTheCountertopMeasurement ?? null,
              comments: null,
              updatedAt: now,
            });

            return {
              status: 'success',
              message: 'Measurement updated (offline)',
              measurement: {
                uuid: effectiveUuid,
                measurementClientInfo: {
                  uuid: tempId,
                  measurementId: null,
                  spoutLevel: data.spoutLevel,
                  kitchenWindowConnectedToTheCountertop:
                    data.kitchenWindowConnectedToTheCountertop,
                  doesTheClientPlanBlindsShutters:
                    data.doesTheClientPlanBlindsShutters,
                  preparationOfOpeningsForTripleLayerInstallation:
                    data.preparationOfOpeningsForTripleLayerInstallation,
                  changeOfOpeningDimensions: data.changeOfOpeningDimensions,
                  constructionSiteCleanupInvestor: data.constructionSiteCleanupInvestor,
                  typeOfInstallationUuids: data.typeOfInstallationUuids,
                  typeOfBlindInstallationUuids:
                    data.typeOfBlindInstallationUuids || [],
                  roomListUuids: data.roomListUuids,
                },
              },
            };
          },
        }
      );
    },
    onMutate: (variables) => {
      Toast({
        message: 'Przetwarzanie danych pomiarowych...',
        type: TOAST_TYPES_ENUM.LOADING,
        id: 'edit-client-info',
      });
      if (options && options.onMutate) {
        return options.onMutate(variables);
      }
    },
    onSuccess: (data, variables, context) => {
      Toast({
        message: 'Dane pomiaru zaktualizowane pomyślnie.',
        type: TOAST_TYPES_ENUM.SUCCESS,
        id: 'edit-client-info',
      });
      queryClient.invalidateQueries({ queryKey: ['clientInfo'] });
      if (options && options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      Toast({
        message: 'Błąd podczas aktualizacji danych pomiaru.',
        type: TOAST_TYPES_ENUM.ERROR,
        id: 'edit-client-info',
      });
      if (options && options.onError) {
        options.onError(error, variables, context);
      }
    },
  });
};

export default usePutGenerallInfoMeasurement;
